/**
 * Stage and percent mapping for the capture pipeline.
 * Pipeline holistic bands: queued 0, upload 0–3, frame-extraction 3–5,
 * SfM 5–50 (complete at 50), Gaussian 51–92, finalizing 92–100.
 */
import { failedStageIndexFromLabel } from '../../utils/failureMetadata';
import {
  FRIENDLY_STAGE_LABELS,
  friendlyStageLabel,
  normalizePipelineStage,
  PIPELINE_STAGE,
  pipelineStageToSegmentIndex,
  resolveCapturePipelineStage,
} from '../../constants/pipelineStages';
import {
  isCaptureMessagingComplete,
  isCaptureUserFacingComplete,
  isCaptureViewerPrepPendingForMessaging,
} from '../../utils/viewerReadiness';
import { failedStageIndexToUiSegment } from '../../constants/captureSchema';
import {
  FINALIZING_PERCENT_END,
  FINALIZING_PERCENT_START,
  GAUSSIAN_PERCENT_END,
  GAUSSIAN_PERCENT_START,
  SFM_PERCENT_END,
  SFM_PERCENT_START,
} from '../../utils/pipeline/pipelinePercentConstants';
import {
  coerceFrameInt,
  deriveFailedStageFromFrameCounts,
  isLowMatchQualityWarning,
  isSfmTooSparseFromFrameCounts,
  LOW_MATCH_WARNING_MAX,
  MIN_REGISTERED_CAMERAS,
  readCaptureFrameFields,
  registrationQualityBucket,
} from '../../utils/pipeline/pipelineFrameFields';
import { normalizeCaptureProfile, captureProfileToBackend } from '../../constants/captureSchema';
import { getMaxVideoDurationSecondsForPlan } from '../../constants/uploadLimits';

export {
  FINALIZING_PERCENT_END,
  FINALIZING_PERCENT_START,
  GAUSSIAN_PERCENT_END,
  GAUSSIAN_PERCENT_START,
  SFM_PERCENT_END,
  SFM_PERCENT_START,
  LOW_MATCH_WARNING_MAX,
  MIN_REGISTERED_CAMERAS,
  coerceFrameInt,
  deriveFailedStageFromFrameCounts,
  isLowMatchQualityWarning,
  isSfmTooSparseFromFrameCounts,
  readCaptureFrameFields,
  registrationQualityBucket,
};

/** Normalize explicit failedStageIndex (1-based canonical or legacy 0-based) to UI segment 0–3. */
export function normalizeFailedStageIndexToUiSegment(explicitIndex, failedAt = null) {
  const fromLabel = failedStageIndexFromLabel(failedAt);
  if (fromLabel >= 0) {
    return fromLabel;
  }
  if (explicitIndex == null || !Number.isFinite(Number(explicitIndex))) {
    return getFailedStageIndex(failedAt);
  }
  const n = Number(explicitIndex);
  if (n >= 4 && n <= 5) {
    return failedStageIndexToUiSegment(n);
  }
  if (n >= 1 && n <= 5) {
    const ui = failedStageIndexToUiSegment(n);
    if (ui >= 0) return ui;
  }
  if (n >= 0 && n <= 3) return n;
  return getFailedStageIndex(failedAt);
}

/** @deprecated Cross-job SfM retries removed — always false. */
export function isPostUploadPipelineRetry() {
  return false;
}

/** @deprecated Auto-exhaustive cross-job retries removed — always false. */
export function isAutoExhaustiveRetryWindow() {
  return false;
}

/** Stale sparse failure metadata must not win UI while Gaussian retrain is in flight. */
export function isExhaustiveRetryInFlight(_capture, { retrainLoading = false } = {}) {
  return retrainLoading === true;
}

/** @deprecated Infra clean cross-job retries removed — always false. */
export function isInfraPipelineCleanRetry() {
  return false;
}

/** True when first-pass SfM failed sparse — user may retry with exhaustive matching. */


/**
 * True when a completed capture registered 3–10 images and should show the viewer warning.
 */
export function isLowMatchQualityCompletedCapture({ processing = {} } = {}) {
  if ((processing.status || '').toLowerCase() !== 'completed') return false;
  const { framesRegistered } = readCaptureFrameFields(processing);
  return isLowMatchQualityWarning(framesRegistered);
}

/** Firestore shows an in-flight pipeline run — stale failedAt from a prior attempt should be ignored. */
export function isActivePipelineRunStatus(status) {
  const s = (status || '').toLowerCase();
  return s === 'processing' || s === 'queued';
}
export const PROGRESS_STALE_POLL_DIVERGENCE = 15;

/**
 * Pick a single holistic % for gallery rows and LoadingUI.
 * After re-run, monotonic poll/cache can stay at a prior-run % while Firestore reports 5% — trust Firestore then.
 */
export function reconcileProcessingPercent(
  firestorePercent,
  candidatePercent,
  processingStatus,
  rawPollPercent = null,
) {
  const fs = firestorePercent != null && Number.isFinite(Number(firestorePercent))
    ? Number(firestorePercent)
    : null;
  const cand = candidatePercent != null && Number.isFinite(Number(candidatePercent))
    ? Number(candidatePercent)
    : null;
  const raw = rawPollPercent != null && Number.isFinite(Number(rawPollPercent))
    ? Number(rawPollPercent)
    : null;

  if (fs == null && cand == null) return null;
  if (fs == null) return cand;
  if (cand == null) return fs;
  const status = (processingStatus || '').toLowerCase();
  if (status === 'failed' || status === 'cancelled') {
    return fs;
  }
  if (!isActivePipelineRunStatus(processingStatus)) return cand;

  const divergence = PROGRESS_STALE_POLL_DIVERGENCE;
  if (cand > fs + divergence) {
    if (raw != null && raw <= fs + divergence) {
      return Math.max(fs, raw);
    }
    // Poll/cache (and raw fetch) from a prior run — trust Firestore.
    return fs;
  }
  return Math.max(fs, cand);
}

/** True while the browser is still uploading the source video (UploadContext phase only). */
export function isClientVideoUploadInProgress(uploadPhase, { useUploadContext = false } = {}) {
  return Boolean(useUploadContext && uploadPhase === 'uploading');
}

/**
 * Upload pipeline segment (index 0) is complete — client upload finished and/or GPU queue has started.
 * Used so QUEUEING never keeps the UPLOAD bar animating.
 */
export function shouldMarkUploadPipelineStageComplete({
  uploadPhase = null,
  useUploadContext = false,
  processingStatus = null,
  runpodJobStatus = null,
  progressMessage = null,
  progressPercent = null,
  runpodInQueue = false,
} = {}) {
  if (isClientVideoUploadInProgress(uploadPhase, { useUploadContext })) return false;
  if (runpodInQueue) return true;
  if (isWorkerQueueingSignal(
    progressMessage,
    runpodJobStatus,
    progressPercent,
    false,
    { processingStatus },
  )) {
    return true;
  }
  if (useUploadContext && (uploadPhase === 'queueing' || uploadPhase === 'pipeline' || uploadPhase === 'done')) {
    return true;
  }
  const proc = (processingStatus || '').toLowerCase();
  if (proc === 'processing' || proc === 'queued') return true;
  // Passive views (e.g. Shopify process modal) only attach to captures after upload.
  return !useUploadContext;
}

/** RunPod states that mean the worker container is executing the pipeline. */
export const RUNPOD_ACTIVE_JOB_STATUSES = new Set(['in_progress', 'running', 'processing']);

/**
 * RunPod job is still starting (queue / pull / early worker) — do not surface FAILED UI yet.
 */
export function isRunpodJobStillStarting(capture, {
  uploadPhase = null,
  useUploadContext = false,
  runpodJobStatus = null,
  pollPercent = null,
  pollStageIndex = null,
  progressMessage = null,
} = {}) {
  const rp = (runpodJobStatus || '').toLowerCase();
  if (rp === 'in_queue' || RUNPOD_ACTIVE_JOB_STATUSES.has(rp)) return true;
  if (useUploadContext && (uploadPhase === 'queueing' || uploadPhase === 'pipeline')) return true;
  if (isLikelyActiveReconstructionRun(capture)) return true;
  if (isStaleFirestorePipelineFailure(capture, { pollPercent, pollStageIndex, runpodJobStatus })) {
    return true;
  }
  if (isCapturePipelineWorkerActive(capture, { pollPercent, pollStageIndex, runpodJobStatus })) {
    return true;
  }
  const proc = (capture?.processing?.status || '').toLowerCase();
  return isWorkerQueueingSignal(
    progressMessage ?? capture?.progress?.message,
    runpodJobStatus,
    pollPercent ?? capture?.progress?.percent,
    false,
    { frames: readCaptureFrameFields(capture?.processing || {}).framesExtracted, processingStatus: proc },
  );
}

function isActivePipelineProgressMessage(message) {
  const m = (message || '').toLowerCase();
  if (!m || m === 'queueing' || m === 'queued') return false;
  if (m.includes('retrying reconstruction')) return false;
  return (
    m.includes('upload')
    || m.includes('extract')
    || m.includes('frame')
    || m.includes('structure')
    || m.includes('sfm')
    || m.includes('3d reconstruction')
    || m.includes('gaussian')
    || m.includes('generating')
    || m.includes('export')
    || m.includes('indexing')
  );
}

/**
 * True when Firestore and/or poll signals show the GPU worker is past queue / dispatch.
 * Used so stale `processing.status: queued` or RunPod `in_queue` cannot mask active SfM.
 */
function hasLivePipelineWorkerEvidence(capture, {
  pollPercent = null,
  pollStageIndex = null,
  runpodJobStatus = null,
} = {}) {
  const rp = (runpodJobStatus || '').toLowerCase();
  if (rp === 'failed' || rp === 'error' || rp === 'cancelled' || rp === 'timed_out') {
    return false;
  }
  if (RUNPOD_ACTIVE_JOB_STATUSES.has(rp) || rp === 'in_queue') return true;
  const fsPct = Number(capture?.progress?.percent);
  if (Number.isFinite(fsPct) && fsPct >= 3) return true;
  const { framesExtracted } = readCaptureFrameFields(capture?.processing || {});
  if (Number.isFinite(framesExtracted) && framesExtracted > 0) return true;
  if (isActivePipelineProgressMessage(capture?.progress?.message)) return true;
  const polled = Number(pollPercent);
  if (Number.isFinite(polled) && polled >= 3) return true;
  const stage = Number(pollStageIndex);
  if (Number.isFinite(stage) && stage >= 2) return true;
  const runpodJobId = String(capture?.progress?.runpodJobId || '').trim();
  if (runpodJobId) {
    const failedAt = String(capture?.processing?.failedAt || '').trim();
    if (!failedAt || failedAt === 'Dispatch') return true;
  }
  return false;
}

/** Firestore still says failed while RunPod / progress proves the worker is running. */
export function isStaleFirestorePipelineFailure(capture, options = {}) {
  if (!capture || typeof capture !== 'object') return false;
  const procStatus = (capture?.processing?.status || '').toLowerCase();
  if (procStatus !== 'failed') return false;
  return hasLivePipelineWorkerEvidence(capture, options);
}

export function isCapturePipelineWorkerActive(capture, {
  pollPercent = null,
  pollStageIndex = null,
  runpodJobStatus = null,
} = {}) {
  const procStatus = (capture?.processing?.status || '').toLowerCase();
  if (procStatus === 'completed' || procStatus === 'cancelled') {
    return false;
  }
  if (procStatus === 'failed') {
    return hasLivePipelineWorkerEvidence(capture, { pollPercent, pollStageIndex, runpodJobStatus });
  }
  if (procStatus === 'queued') {
    return hasWorkerFoundEvidence({
      processingStatus: procStatus,
      progressPercent: capture?.progress?.percent,
      progressMessage: capture?.progress?.message,
      runpodJobStatus,
      pollPercent,
      pollStageIndex,
      framesExtracted: readCaptureFrameFields(capture?.processing || {}).framesExtracted,
    });
  }
  const fsPct = Number(capture?.progress?.percent);
  if (Number.isFinite(fsPct) && fsPct >= 2) return true;
  const { framesExtracted } = readCaptureFrameFields(capture?.processing || {});
  if (Number.isFinite(framesExtracted) && framesExtracted > 0) return true;
  if (isActivePipelineProgressMessage(capture?.progress?.message)) return true;
  if (procStatus === 'processing') return true;

  const polled = Number(pollPercent);
  if (Number.isFinite(polled) && polled >= 2) return true;
  const stage = Number(pollStageIndex);
  if (Number.isFinite(stage) && stage >= 2) return true;
  const rp = (runpodJobStatus || '').toLowerCase();
  if (RUNPOD_ACTIVE_JOB_STATUSES.has(rp)) return true;
  return false;
}

/**
 * True when pipeline progress should stay on the GPU queue pill (0% / QUEUEING).
 * Unlike the old heuristic, does not treat every 0% snapshot as queueing — worker
 * messages, frames, and processing status must be absent first.
 */
export function isWorkerQueueingSignal(
  message,
  runpodJobStatus,
  pipelinePercent,
  workerStarted,
  { frames = null, processingStatus = null } = {},
) {
  if (isCapturePipelineWorkerActive(
    { processing: { status: processingStatus, frames }, progress: { message, percent: pipelinePercent } },
    { runpodJobStatus },
  )) {
    return false;
  }
  const rp = (runpodJobStatus || '').toLowerCase();
  if (rp === 'in_queue') return true;
  const proc = (processingStatus || '').toLowerCase();
  if (proc === 'queued' && !workerStarted && (pipelinePercent ?? 0) === 0) return true;
  const m = (message || '').toLowerCase();
  if (m.includes('setting up pipeline') || m.includes('setting up reconstruction')) return true;
  if (m.includes('in queue') || m === 'queued' || m === 'queueing') return true;
  return false;
}

/**
 * LoadingUI / reconnect queue state — only when there is no evidence the worker has started.
 */
export function shouldShowGpuQueueUi(capture, {
  pollPercent = null,
  pollStageIndex = null,
  pollStageTitle = null,
  runpodJobStatus = null,
} = {}) {
  if (isCapturePipelineWorkerActive(capture, { pollPercent, pollStageIndex, runpodJobStatus })) {
    return false;
  }
  const procStatus = (capture?.processing?.status || '').toLowerCase();
  const rp = (runpodJobStatus || '').toLowerCase();
  if (rp === 'in_queue') return true;
  if (procStatus === 'queued') return true;
  const msg = (capture?.progress?.message || '').toLowerCase();
  if (msg === 'queueing' || msg === 'queued' || msg.includes('in queue')) return true;
  const stage = pollStageIndex;
  if (
    (pollStageTitle === 'QUEUEING' || pollStageTitle === 'QUEUED' || pollStageTitle === 'IN QUEUE')
    && (stage == null || Number(stage) < 0)
  ) {
    return true;
  }
  return false;
}

/** Worker-stage progress messages (not client upload, not pure queue). */
function isWorkerStageProgressMessage(message) {
  const m = (message || '').toLowerCase();
  if (!m) return false;
  if (m === 'queueing' || m === 'queued' || m.includes('in queue')) return false;
  if (m.includes('setting up pipeline') || m.includes('setting up reconstruction')) return false;
  return (
    m.includes('uploading video')
    || m.includes('upload complete')
    || m.includes('extracting frames')
    || (m.includes('frame') && m.includes('extract'))
    || m.includes('structure from motion')
    || m.includes('running 3d reconstruction')
    || m.includes('sfm')
    || m.includes('gaussian')
    || m.includes('generating')
    || m.includes('finalizing')
    || m.includes('viewer ready')
    || m.includes('exporting')
  );
}

/**
 * True once a GPU worker has taken the job (RunPod active, first callback at 2%, or worker-stage message).
 * Stale framesExtracted alone does not count while status is still queued.
 */
export function hasWorkerFoundEvidence({
  processingStatus = null,
  pipelineStage = null,
  progressPercent = null,
  progressMessage = null,
  runpodJobStatus = null,
  pollPercent = null,
  pollStageIndex = null,
  pollPipelineStage = null,
  framesExtracted = null,
  infraPipelineRestart = false,
} = {}) {
  const proc = (processingStatus || '').toLowerCase();
  const rp = (runpodJobStatus || '').toLowerCase();
  if (RUNPOD_ACTIVE_JOB_STATUSES.has(rp)) return true;

  if (infraPipelineRestart) {
    const fsPct = progressPercent != null && Number.isFinite(Number(progressPercent))
      ? Number(progressPercent)
      : null;
    if (fsPct != null && fsPct >= 2) return true;
    const m = (progressMessage || '').toLowerCase();
    if (
      m.includes('extracting frames')
      || (m.includes('frame') && m.includes('extract'))
      || m.includes('uploading video')
    ) {
      return true;
    }
    return false;
  }

  const stage = normalizePipelineStage(pipelineStage) ?? normalizePipelineStage(pollPipelineStage);
  if (stage && stage !== PIPELINE_STAGE.UPLOADING) return true;

  const fsPct = progressPercent != null && Number.isFinite(Number(progressPercent))
    ? Number(progressPercent)
    : null;
  const polled = pollPercent != null && Number.isFinite(Number(pollPercent))
    ? Number(pollPercent)
    : null;
  const effectivePct = polled != null
    ? (fsPct != null ? Math.max(fsPct, polled) : polled)
    : fsPct;
  if (effectivePct != null && effectivePct >= 2) return true;

  const pollStage = Number(pollStageIndex);
  if (Number.isFinite(pollStage) && pollStage >= 2) return true;

  if (isWorkerStageProgressMessage(progressMessage)) return true;

  if (proc !== 'queued') {
    const fe = coerceFrameInt(framesExtracted);
    if (fe != null && fe > 0) return true;
  }

  return false;
}

/**
 * Single source of truth for pipeline stage across capture cards, LoadingUI, and ReconstructionPipeline.
 * Returns { stageKey, title, isQueueing, segmentIndex, pipelineStage, internalStage }.
 */
export function resolveCanonicalPipelineStage({
  isClientUploading = false,
  processingStatus = null,
  pipelineStage = null,
  progressPercent = null,
  progressMessage = null,
  runpodJobStatus = null,
  pollPercent = null,
  pollStageIndex = null,
  pollStageTitle = null,
  pollPipelineStage = null,
  framesExtracted = null,
  framesRegistered = null,
  sfmMatchingMethod = null,
  infraRetryCount = null,
  failureCode = null,
  failedAt = null,
} = {}) {
  const proc = (processingStatus || '').toLowerCase();
  const captureLike = {
    processing: {
      status: processingStatus,
      stage: pipelineStage,
      sfmMatchingMethod,
      infraRetryCount,
      failureCode,
      failedAt,
      framesRegistered,
      framesExtracted,
    },
    progress: { percent: progressPercent ?? pollPercent, message: progressMessage },
    pollPipelineStage,
  };

  const infraPipelineRestart = isInfraPipelineCleanRetry(captureLike);

  const workerFound = hasWorkerFoundEvidence({
    processingStatus,
    pipelineStage,
    progressPercent,
    progressMessage,
    runpodJobStatus,
    pollPercent,
    pollStageIndex,
    pollPipelineStage,
    framesExtracted,
    infraPipelineRestart,
  });

  const queueTitle = pollStageTitle && ['QUEUEING', 'QUEUED', 'IN QUEUE'].includes(String(pollStageTitle).toUpperCase());
  const queueMessage = String(progressMessage || '').toLowerCase() === 'queueing'
    || String(progressMessage || '').toLowerCase() === 'queued';
  const runpodInQueue = (runpodJobStatus || '').toLowerCase() === 'in_queue';

  if (!isClientUploading && !workerFound && (proc === 'queued' || runpodInQueue || queueMessage || queueTitle)) {
    return {
      stageKey: 'QUEUEING',
      title: 'QUEUEING',
      isQueueing: true,
      segmentIndex: -1,
      pipelineStage: null,
      internalStage: -1,
    };
  }

  let resolvedStage = resolveCapturePipelineStage(captureLike, { isClientUploading });
  if (resolvedStage === PIPELINE_STAGE.DONE || proc === 'completed') {
    return {
      stageKey: 'COMPLETE',
      title: 'RECONSTRUCTION COMPLETE',
      isQueueing: false,
      segmentIndex: 4,
      pipelineStage: PIPELINE_STAGE.DONE,
      internalStage: 6,
    };
  }

  // Frames on disk means extraction has at least started — never fall back to UPLOAD/PROCESSING.
  const extracted = Number(framesExtracted);
  if (
    !resolvedStage
    && Number.isFinite(extracted)
    && extracted > 0
    && (proc === 'processing' || proc === 'queued')
  ) {
    resolvedStage = PIPELINE_STAGE.ANALYZING_FRAMES;
  }

  const segmentIndex = pipelineStageToSegmentIndex(resolvedStage);
  const title = friendlyStageLabel(resolvedStage).toUpperCase();
  const stageKeys = ['UPLOADING_VIDEO', 'ANALYZING_FRAMES', 'BUILDING_STRUCTURE', 'RENDERING'];
  const internalMap = [1, 2, 3, 4];

  return {
    stageKey: segmentIndex >= 0 ? stageKeys[segmentIndex] : 'PROCESSING',
    title: title || 'PROCESSING',
    isQueueing: false,
    segmentIndex: segmentIndex >= 0 ? segmentIndex : 0,
    pipelineStage: resolvedStage,
    internalStage: segmentIndex >= 0 ? internalMap[segmentIndex] : 2,
  };
}

/** Mirror backend frame clamp (``sfm/frame_policy.py`` + plan duration caps). */
const PIPELINE_FRAME_POLICY = {
  orbital: { rate: 2.0, min: 60, defaultTarget: 150 },
  equirect_360: { rate: 0.5, min: 30, max: 80, defaultTarget: 50 },
};

/**
 * Expected SfM frame budget from source duration + capture profile.
 * @param {number | null | undefined} durationSeconds
 * @param {string} [captureProfile]
 * @param {string | null | undefined} [billingPlanAtStart] — when set, orbital max = plan duration × 2fps
 */
export function estimatePipelineFrameTarget(durationSeconds, captureProfile = 'orbital', billingPlanAtStart = null) {
  const backendProfile = captureProfileToBackend(normalizeCaptureProfile(captureProfile));
  const policy = PIPELINE_FRAME_POLICY[backendProfile] || PIPELINE_FRAME_POLICY.orbital;
  let maxFrames = policy.max ?? null;
  if (backendProfile === 'orbital') {
    const planMaxSec = getMaxVideoDurationSecondsForPlan(billingPlanAtStart || 'free');
    maxFrames = Math.round(planMaxSec * policy.rate);
  }
  const d = Number(durationSeconds);
  if (!Number.isFinite(d) || d <= 0) {
    if (backendProfile === 'orbital' && maxFrames != null) {
      return Math.max(policy.min, maxFrames);
    }
    return policy.defaultTarget ?? policy.min;
  }
  const n = Math.round(d * policy.rate);
  if (maxFrames != null) {
    return Math.max(policy.min, Math.min(maxFrames, n));
  }
  return Math.max(policy.min, Math.min(policy.max, n));
}

function parseResolutionPixels(sourceVideoResolutionFps) {
  if (typeof sourceVideoResolutionFps !== 'string') return null;
  const match = sourceVideoResolutionFps.match(/(\d{2,5})\s*x\s*(\d{2,5})/i);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return width * height;
}

/**
 * Total pipeline ETA (upload → SfM → Gaussian → finalizing export + SOG).
 * Calibrated to backend frame budget: short clips still process ~100+ frames (SfM + 7k Gaussian iters).
 * Rounded to nearest 5 minutes.
 */
export function estimateProcessingMinutes({
  durationSeconds,
  sourceVideoResolutionFps,
  captureProfile,
  billingPlanAtStart = null,
} = {}) {
  const durationSec = Number(durationSeconds);
  const hasDuration = Number.isFinite(durationSec) && durationSec > 0;
  if (!hasDuration) return null;

  const frameTarget = estimatePipelineFrameTarget(durationSec, captureProfile, billingPlanAtStart);
  const baselinePixels = 1920 * 1080;
  const pixelCount = parseResolutionPixels(sourceVideoResolutionFps) ?? baselinePixels;
  const resolutionFactor = Math.min(2.4, Math.max(0.75, Math.sqrt(pixelCount / baselinePixels)));

  /** Gaussian iteration floor + SfM setup + export/SOG — significant even for sub‑10s clips. */
  const FIXED_PIPELINE_MINUTES = 18;
  /** SfM + training load scales with COLMAP frame budget. */
  const MINUTES_PER_FRAME = 0.14;
  /** Upload + decode overhead on longer source files. */
  const MINUTES_PER_SOURCE_MINUTE = 1.5;

  const sourceMinutes = durationSec / 60;
  const coreMinutes =
    (FIXED_PIPELINE_MINUTES + frameTarget * MINUTES_PER_FRAME + sourceMinutes * MINUTES_PER_SOURCE_MINUTE) *
    resolutionFactor;
  const estimatedMinutes = coreMinutes + 3;
  return Math.min(300, Math.max(15, Math.ceil(estimatedMinutes / 5) * 5));
}

/** Human-readable byte count for upload subtitle (e.g. `42 MB / 180 MB`). */
export function formatBytes(n) {
  const bytes = Number.isFinite(n) ? Math.max(0, n) : 0;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
  return `${Math.round(bytes)} B`;
}

export function messageToStage(message = '') {
  const n = (message || '').toLowerCase();
  if (n.includes('viewer ready') || (n.includes('training complete') && n.includes('export ready'))) return 6;
  if (
    n.includes('finalizing')
    || n.includes('preparing viewer')
    || n.includes('preparing 3d viewer')
    || n.includes('exporting gaussians')
    || (n.includes('exporting') && n.includes('uploading'))
    || n.includes('exporting gaussian splat')
    || n.includes('exporting gaussian capture')
    || n.includes('uploading new splat to cloud')
    || n.includes('uploading new capture to cloud')
  ) return 5;
  if (n.includes('gaussian') || (n.includes('initiating') && n.includes('training')) || n.includes('generating gaussians')) return 4;
  if (
    n.includes('structure from motion')
    || n.includes('running structure from motion')
    || n.includes('running 3d reconstruction')
    || n.includes('sfm in progress')
    || (n.includes('sfm') && n.includes('complete'))
  ) {
    return 3;
  }
  if (n.includes('sfm process started') || (n.includes('initiating') && n.includes('sfm'))) return 3;
  if (
    (n.includes('frame') && n.includes('extract'))
    || n.includes('extracting frames')
    || n.includes('extracting images')
    || (n.includes('image') && n.includes('extract'))
  ) return 2;
  // Keep upload messaging in upload stage; extraction starts only on extraction-specific signals.
  if (n.includes('uploading video') || n.includes('upload complete')) return 1;
  if (n.includes('upload') && !n.includes('complete')) return 1;
  return 0;
}

export function normalizeProgressMessage(message = '') {
  const n = (message || '').toLowerCase();
  if (!n) return 'UPLOADING VIDEO';
  if (n.includes('setting up pipeline')) return 'SETTING UP PIPELINE';
  if (n.includes('in queue') || n === 'queued') return 'QUEUEING';
  if (n.includes('viewer ready')) return 'VIEWER READY';
  if (n.includes('preparing viewer') || n.includes('preparing 3d viewer')) return 'PREPARING VIEWER';
  if (n.includes('finalizing')) return 'FINALIZING';
  if (
    n.includes('exporting gaussians')
    || n.includes('uploading new splat to cloud')
    || n.includes('uploading new capture to cloud')
    || n.includes('exporting gaussian splat')
    || n.includes('exporting gaussian capture')
  ) return 'FINALIZING';
  if (n.includes('generating gaussians') || n.includes('gaussian') || (n.includes('initiating') && n.includes('training'))) return 'GENERATING GAUSSIANS';
  if (
    n.includes('structure from motion')
    || n.includes('running structure from motion')
    || n.includes('running 3d reconstruction')
    || n.includes('sfm in progress')
    || (n.includes('sfm') && n.includes('complete'))
  ) {
    return 'STRUCTURE FROM MOTION';
  }
  if (n.includes('sfm process started') || (n.includes('frame') && n.includes('extract')) || n.includes('extracting frames') || ((n.includes('initiating') && n.includes('sfm')))) return 'EXTRACTING IMAGES';
  if (n.includes('upload') || n.includes('received upload request')) return 'UPLOADING VIDEO';
  return message.toString().toUpperCase();
}

export function normalizeStatusTitle(title = '') {
  const normalized = (title || '').toString().trim().toUpperCase();
  if (!normalized) return '';
  if (normalized === 'UPLOAD') return 'UPLOADING';
  if (normalized === 'IN QUEUE' || normalized === 'QUEUED') return 'QUEUEING';
  return normalized;
}


export function getInterruptedServersOfflineTitle() {
  return 'FAILED: SERVERS OFFLINE';
}

/**
 * Cap percent by current stage (truthful/steady profile).
 */
export function getStagePercentCap(currentStage) {
  if (currentStage == null || currentStage < 0) return 0;
  if (currentStage <= 1) return 3;
  if (currentStage <= 2) return 5;
  if (currentStage <= 3) return SFM_PERCENT_END;
  if (currentStage <= 4) return GAUSSIAN_PERCENT_END;
  return 100;
}

/**
 * Start/end percent for each stage segment (for interpolation ramp).
 */
export function getStagePercentRange(stageIndex) {
  if (stageIndex == null || stageIndex < 0) return { start: 0, end: 3 };
  if (stageIndex <= 1) return { start: 0, end: 3 };
  if (stageIndex <= 2) return { start: 3, end: 5 };
  if (stageIndex <= 3) return { start: SFM_PERCENT_START, end: SFM_PERCENT_END };
  if (stageIndex <= 4) return { start: GAUSSIAN_PERCENT_START, end: GAUSSIAN_PERCENT_END };
  return { start: FINALIZING_PERCENT_START, end: 100 };
}

/**
 * Infer canonical fail stage from error message when backend does not send failStage.
 * Returns null when stage cannot be inferred (caller should use percent/message fallback).
 */
export function inferFailStageFromError(error = '') {
  const n = (error || '').toLowerCase();
  if (n.includes('sfm') && n.includes('fail')) return 'SFM';
  if (n.includes('gaussian') && n.includes('train')) return 'Gaussian Training';
  if (n.includes('gaussian') && n.includes('export')) return 'Gaussian Training';
  if (n.includes('export') && (n.includes('config') || n.includes('not found'))) return 'Gaussian Training';
  if (n.includes('frame') && n.includes('extract')) return 'Image Extraction';
  if (n.includes('upload') || n.includes('video') || n.includes('download')) return 'Video Upload';
  if (n.includes('dispatch') || n.includes('gpu worker') || n.includes('no healthy gpu')) return 'Dispatch';
  return null;
}

/**
 * Map failedAt/failStage to stage index (0-3) for dot indicator.
 * Upload=0, FrameExtraction=1, SfM=2, GaussianTraining/GaussianExport=3.
 */
export function getFailedStageIndex(failedAt) {
  if (!failedAt || typeof failedAt !== 'string') return -1;
  const n = failedAt.toLowerCase();
  if (n === 'dispatch' || n === 'cancelled') return -1;
  if (n === 'upload' || n.includes('upload')) return 0;
  if (
    n === 'frame extraction'
    || n === 'image extraction'
    || (n.includes('extract') && (n.includes('frame') || n.includes('image')))
  ) return 1;
  if (n === 'sfm' || n.includes('structure from motion')) return 2;
  if (n.includes('sog') || n.includes('finalizing') || n.includes('viewer ready')) return 3;
  if (n === 'gaussian training' || n.includes('gaussian') || n.includes('export')) return 3;
  return -1;
}

/**
 * Human-readable stage label for a pipeline stage index (0-3).
 */
export function stageIndexToLabel(idx) {
  return (FRIENDLY_STAGE_LABELS[idx] ?? 'UNKNOWN').toUpperCase();
}

/**
 * Max internal pipeline stage (1–6) allowed for a holistic progress percent — matches bands in file header.
 * Internal: 1 upload, 2 frame extraction, 3 SfM, 4 Gaussian, 5 export/prep, 6 done.
 */
export function maxInternalStageIndexForHolisticPercent(percent) {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  if (pct >= 100) return 6;
  if (pct >= FINALIZING_PERCENT_START) return 5;
  if (pct >= GAUSSIAN_PERCENT_START) return 4;
  if (pct >= SFM_PERCENT_START) return 3;
  if (pct >= 4) return 2;
  return 1;
}

/**
 * Clamp inferred Firestore/redis stage so UI cannot show Gaussian/SfM ahead of holistic % (e.g. noisy "generating" + 5%).
 */
export function clampInternalStageIndexToHolisticPercent(stageIndex, percent) {
  const s = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) ? stageIndex : 0;
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  if (s < 0) return s;
  if (s >= 6) return s;
  const maxS = maxInternalStageIndexForHolisticPercent(pct);
  return Math.min(s, maxS);
}

/** LoadingUI / poll title when stage index is known (internal 1–5). */
export function loadingTitleForInternalPipelineStage(internalStage, _percent) {
  const s = typeof internalStage === 'number' && !Number.isNaN(internalStage) ? internalStage : 0;
  if (s <= 0) return 'QUEUEING';
  if (s <= 1) return 'UPLOADING VIDEO';
  if (s === 2) return 'IMAGE EXTRACTION';
  if (s === 3) return 'STRUCTURE FROM MOTION';
  if (s === 4) return 'GAUSSIAN TRAINING';
  if (s === 5) return 'FINALIZING';
  return 'RECONSTRUCTION COMPLETE';
}

/** Pill title from holistic pipeline percent and optional backend message. */
export function loadingTitleForPipelinePercent(percent, message = '') {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const m = (message || '').toLowerCase();
  if (pct >= 100 || m.includes('viewer ready') || (m.includes('training complete') && m.includes('export ready'))) {
    return 'RECONSTRUCTION COMPLETE';
  }
  if (m.includes('preparing viewer') || m.includes('preparing 3d viewer')) return 'PREPARING VIEWER';
  if (m.includes('finalizing')) return 'FINALIZING';
  if (pct >= FINALIZING_PERCENT_START || m.includes('exporting gaussians') || m.includes('exporting gaussian')) {
    return 'FINALIZING';
  }
  if (
    m.includes('structure from motion')
    || m.includes('running 3d reconstruction')
    || (m.includes('sfm') && !m.includes('gaussian'))
  ) {
    return 'STRUCTURE FROM MOTION';
  }
  if (
    m.includes('generating gaussians')
    || (m.includes('gaussian') && !m.includes('sfm'))
    || (m.includes('initiating') && m.includes('training'))
  ) {
    return 'GAUSSIAN TRAINING';
  }
  if (pct >= GAUSSIAN_PERCENT_START && pct < FINALIZING_PERCENT_START) return 'GAUSSIAN TRAINING';
  if (pct >= SFM_PERCENT_START && pct <= SFM_PERCENT_END) return 'STRUCTURE FROM MOTION';
  if (m.includes('uploading video') || m.includes('upload complete')) return 'UPLOADING VIDEO';
  if (
    m.includes('extracting frames')
    || m.includes('extracting images')
    || (m.includes('frame') && m.includes('extract'))
    || (m.includes('image') && m.includes('extract'))
    || (pct != null && pct >= 2 && pct < SFM_PERCENT_START)
  ) {
    return 'IMAGE EXTRACTION';
  }
  if (
    m.includes('in queue')
    || m === 'queued'
    || m === 'queueing'
    || m.includes('setting up pipeline')
    || m.includes('setting up reconstruction')
  ) {
    return 'QUEUEING';
  }
  return 'PROCESSING';
}

/**
 * When Firestore has a frame count, extraction is done for UX even if Redis still reports
 * upload/extraction. Bump to stage 4 (SfM) so the top pipeline, LoadingUI, and % interpolation match.
 * Does not change queued (-1) or Gaussian/export (≥4) stages.
 */
export function alignPipelineStageWithExtractedFrames(stageIndex, framesCount, pipelineBusy, {
  processingStatus = null,
} = {}) {
  const frames = Number(framesCount);
  const s = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) ? stageIndex : 0;
  if ((processingStatus || '').toLowerCase() === 'queued') return s;
  if (!pipelineBusy) return s;
  if (!Number.isFinite(frames) || frames <= 0) return s;
  if (s < 0) return s;
  if (s >= 3) return s;
  return 3;
}

/**
 * Map currentStage (internal index 1–6) to pipeline stage index (0–3) for Reconstruction Pipeline bar.
 * 1→0 (Upload in progress); 0 / −1→1 (post-upload queue — upload segment complete);
 * 2→1 (Frame Extraction); 3→2 (SfM); 4→3 (Gaussian); 5→3 (Finalizing); 6→3 (all done).
 */
export function getPipelineStageIndex(currentStage) {
  if (currentStage == null) return 0;
  const s = Number(currentStage);
  if (Number.isNaN(s)) return 0;
  if (s <= 0) return 1;
  if (s <= 1) return 0;
  if (s <= 2) return 1;
  if (s <= 3) return 2;
  return 3;
}

/** True when runtime signals show the worker is past client video upload. */
export function isPipelinePastClientUpload({
  canonicalStage = null,
  currentStage = null,
  framesExtracted = null,
  uploadPhase = null,
  useUploadContext = false,
} = {}) {
  const extracted = Number(framesExtracted);
  if (Number.isFinite(extracted) && extracted > 0) {
    return true;
  }
  if (!canonicalStage || canonicalStage.isQueueing) return false;
  if (Number.isFinite(Number(canonicalStage.segmentIndex)) && Number(canonicalStage.segmentIndex) >= 1) {
    return true;
  }
  if (Number.isFinite(Number(canonicalStage.internalStage)) && Number(canonicalStage.internalStage) >= 2) {
    return true;
  }
  // During active client upload, a noisy runtime poll stage can briefly jump to extraction.
  // Do not treat runtime stage alone as authoritative until upload has handed off.
  if (useUploadContext && uploadPhase === 'uploading') {
    return false;
  }
  const runtimeStage = Number(currentStage);
  if (Number.isFinite(runtimeStage) && runtimeStage >= 2) return true;
  if (getPipelineStageIndex(currentStage) >= 1) return true;
  return false;
}

/** Elevate 4-step pipeline segment when Firestore lags Redis poll / frame metrics. */
export function resolveElevatedPipelineSegmentIndex({
  canonicalSegmentIndex = 0,
  currentStage = null,
  framesExtracted = null,
  isQueueing = false,
} = {}) {
  if (isQueueing) return -1;
  let seg = Number.isFinite(Number(canonicalSegmentIndex)) ? Math.max(0, Number(canonicalSegmentIndex)) : 0;
  const fromRuntime = getPipelineStageIndex(currentStage);
  if (Number.isFinite(fromRuntime) && fromRuntime >= 0) {
    seg = Math.max(seg, fromRuntime);
  }
  const extracted = Number(framesExtracted);
  if (Number.isFinite(extracted) && extracted > 0) {
    seg = Math.max(seg, 1);
  }
  return seg;
}

/** Prefer runtime poll stage title when canonical metadata is still on upload. */
export function resolveLoadingUiStageTitle(canonicalStage, {
  currentStage = null,
  progressPercent = null,
} = {}) {
  if (!canonicalStage || canonicalStage.isQueueing) {
    return canonicalStage?.title || 'QUEUEING';
  }
  const canonicalTitle = canonicalStage.title || 'PROCESSING';
  const runtimeStage = Number(currentStage);
  const runtimeTitle = Number.isFinite(runtimeStage) && runtimeStage >= 2
    ? loadingTitleForInternalPipelineStage(runtimeStage, progressPercent)
    : null;
  const uploadLike = (
    canonicalTitle === 'VIDEO UPLOAD'
    || canonicalTitle === 'UPLOADING VIDEO'
    || canonicalTitle === 'PROCESSING'
  );
  if (runtimeTitle && uploadLike && runtimeTitle !== 'UPLOADING VIDEO') {
    return runtimeTitle;
  }
  return canonicalTitle;
}

/**
 * Compute active and failed indices for Reconstruction Pipeline.
 * Returns { activeIndex: 0–4, failedIndex: 0–3 | -1 }.
 */
export function stageIndexForPipeline(currentStage, status, failedAt, explicitFailedStageIndex = null) {
  let failedIndex = -1;
  if (explicitFailedStageIndex != null && Number.isFinite(Number(explicitFailedStageIndex))) {
    failedIndex = failedStageIndexToUiSegment(Number(explicitFailedStageIndex));
  }
  if (failedIndex < 0) {
    failedIndex = getFailedStageIndex(failedAt);
  }
  if (status === 'failed' && failedIndex >= 0) {
    return { activeIndex: failedIndex, failedIndex };
  }
  if (status === 'completed') {
    return { activeIndex: 3, failedIndex: -1 };
  }
  const activeIndex = getPipelineStageIndex(currentStage);
  return { activeIndex, failedIndex: -1 };
}

/**
 * Clamp holistic display % to the band for a resolved internal stage index (1–6).
 * Stage title bounds percentage — stale poll/cache cannot show SfM above 50% or Gaussian below 51%.
 */
export function clampDisplayPercentByStageIndex(stageIndex, percent) {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const s = typeof stageIndex === 'number' && !Number.isNaN(stageIndex) ? stageIndex : 0;
  if (s <= 0) return pct;
  if (s <= 1) return Math.min(pct, 3);
  if (s === 2) return Math.min(Math.max(pct, 3), 5);
  if (s === 3) return Math.min(Math.max(pct, SFM_PERCENT_START), SFM_PERCENT_END);
  if (s === 4) return Math.min(Math.max(pct, GAUSSIAN_PERCENT_START), GAUSSIAN_PERCENT_END);
  if (s === 5) return Math.max(pct, FINALIZING_PERCENT_START);
  if (s >= 6) return pct >= 100 ? 100 : Math.max(pct, FINALIZING_PERCENT_START);
  return pct;
}

/**
 * Merge Redis stage key, Firestore progress message, and holistic % into one display stage.
 * Used by usePipelinePolling so LoadingUI and ReconstructionPipeline stay aligned.
 */
export function resolvePolledPipelineDisplay({
  redisStage = '',
  percent = 0,
  progressMessage = '',
  framesCount,
  pipelineBusy = false,
}) {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const effectiveStage = (redisStage || '').trim() || 'queued';
  const fromRedis = mapRedisStageToFrontend(effectiveStage, pct);
  let stageIndex = fromRedis.stageIndex;
  let stageTitle = fromRedis.stageTitle;

  const msg = (progressMessage || '').trim();
  if (msg) {
    const fromMsg = messageToStage(msg);
    if (fromMsg > stageIndex) {
      stageIndex = fromMsg;
      stageTitle = loadingTitleForPipelinePercent(pct, msg);
    }
    const titleFromMsg = loadingTitleForPipelinePercent(pct, msg);
    if (titleFromMsg !== 'PROCESSING') {
      stageTitle = titleFromMsg;
    }
  }

  const preAlign = stageIndex;
  let displayStageIndex = alignPipelineStageWithExtractedFrames(
    stageIndex,
    framesCount,
    pipelineBusy,
  );
  let displayStageTitle =
    displayStageIndex !== preAlign && preAlign >= 0 && preAlign < 3
      ? 'ANALYZING IMAGES'
      : stageTitle;

  const alignedBeforeClamp = displayStageIndex;
  displayStageIndex = clampInternalStageIndexToHolisticPercent(displayStageIndex, pct);

  // Stale low % must not undo frames-based SfM promotion (logs can be ahead of Firestore).
  const frames = Number(framesCount);
  if (
    pipelineBusy
    && alignedBeforeClamp >= 3
    && preAlign < 3
    && Number.isFinite(frames)
    && frames > 0
    && displayStageIndex < alignedBeforeClamp
  ) {
    displayStageIndex = alignedBeforeClamp;
    displayStageTitle = 'STRUCTURE FROM MOTION';
  }

  // Redis stage key (local /pipeline/status) is authoritative when ahead of stale percent.
  if (pipelineBusy && fromRedis.stageIndex >= 3 && displayStageIndex < fromRedis.stageIndex) {
    displayStageIndex = fromRedis.stageIndex;
    displayStageTitle = fromRedis.stageTitle;
  }

  if (displayStageIndex !== alignedBeforeClamp && displayStageIndex === clampInternalStageIndexToHolisticPercent(alignedBeforeClamp, pct)) {
    displayStageTitle = loadingTitleForInternalPipelineStage(displayStageIndex, pct);
  }

  let displayPercent = pct;
  const sfmNudge =
    displayStageIndex === 3
    && preAlign >= 0
    && preAlign < 3
    && pipelineBusy;
  if (sfmNudge) {
    displayPercent = Math.min(SFM_PERCENT_END, Math.max(pct, SFM_PERCENT_START));
  }

  displayPercent = clampDisplayPercentByStageIndex(displayStageIndex, displayPercent);

  return {
    rawStageIndex: fromRedis.stageIndex,
    stageIndex: displayStageIndex,
    stageTitle: displayStageTitle,
    displayPercent,
  };
}

/**
 * Map Redis pipeline stage string to frontend stage index and title.
 * Used by usePipelinePolling to translate backend progress into LoadingUI props.
 */
export function mapRedisStageToFrontend(stage, percent) {
  const s = (stage || '').toLowerCase();
  const pct = Number.isFinite(percent) ? percent : 0;
  if (s === 'done') return { stageIndex: 6, stageTitle: 'RECONSTRUCTION COMPLETE' };
  if (s === 'viewerprep' || s === 'viewerpreparing' || s === 'preparingviewer' || s === 'finalizing') {
    return { stageIndex: 5, stageTitle: 'FINALIZING' };
  }
  if (s === 'gaussianexport' || s === 'sogconversion') {
    return { stageIndex: 5, stageTitle: 'FINALIZING' };
  }
  if (s === 'gaussiantraining') return { stageIndex: 4, stageTitle: 'GAUSSIAN TRAINING' };
  if (s === 'sfmcomplete') return { stageIndex: 3, stageTitle: 'STRUCTURE FROM MOTION' };
  if (s === 'sfm') return { stageIndex: 3, stageTitle: 'STRUCTURE FROM MOTION' };
  if (s === 'stitching360' || s === 'stitching') {
    return { stageIndex: 1, stageTitle: 'STITCHING 360 VIDEO' };
  }
  if (s === 'frameextraction') {
    return { stageIndex: 2, stageTitle: 'IMAGE EXTRACTION' };
  }
  if (s.includes('frame') && (s.includes('extract') || s.includes('extraction'))) {
    return { stageIndex: 2, stageTitle: 'IMAGE EXTRACTION' };
  }
  if (s === 'video2images' || s === 'extracting' || s === 'extraction') {
    return { stageIndex: 2, stageTitle: 'IMAGE EXTRACTION' };
  }
  if (s === 'upload' || s === 'uploadpending') return { stageIndex: 1, stageTitle: 'VIDEO UPLOAD' };
  if (s === 'queued') return { stageIndex: -1, stageTitle: 'QUEUEING' };
  if (pct >= FINALIZING_PERCENT_START && pct < 100) {
    return { stageIndex: 5, stageTitle: 'FINALIZING' };
  }
  return { stageIndex: 0, stageTitle: 'PROCESSING' };
}

export function isLikelyActiveReconstructionRun(capture) {
  if (!capture || typeof capture !== 'object') return false;
  const procStatus = (capture.processing?.status || '').toLowerCase();
  if (!isActivePipelineRunStatus(procStatus)) return false;
  const pct = Number(capture.progress?.percent);
  const msg = (capture.progress?.message || '').toLowerCase();
  const updated = capture.updatedAt?.toMillis?.() ?? 0;
  const ageMs = updated ? Date.now() - updated : Number.POSITIVE_INFINITY;
  if (ageMs > 10 * 60 * 1000) return false;
  if (Number.isFinite(pct) && pct <= 5 && (msg.includes('upload') || msg.includes('queue'))) {
    return true;
  }
  return false;
}

/** Deliverable splat/viewer output exists — user can see the scene in the viewer. */
export function isCaptureOutputDelivered(capture, { isActiveReupload = false } = {}) {
  if (isActiveReupload || !capture || typeof capture !== 'object') return false;
  return isCaptureUserFacingComplete(capture);
}

/** UI-facing processing status — never show queue/processing when viewer asset is messaging-ready. */
export function resolveCaptureProcessingStatusForUi(capture, { isActiveReupload = false } = {}) {
  const raw = (capture?.processing?.status || 'processing').toLowerCase();
  if (raw === 'failed' || raw === 'cancelled') return raw;
  if (isCaptureMessagingComplete(capture, { isActiveReupload })) return 'completed';
  if (raw === 'completed') return 'processing';
  if (isCaptureViewerPrepPendingForMessaging(capture, { isActiveReupload })) return 'processing';
  return raw;
}